(() => {
    let viewportHeight = window.innerHeight;

    const nodeData = new WeakMap();
    const activeElements = new Set();
    const allElements = new Set();

    let currentScrollY = window.scrollY || window.pageYOffset;

    const lerp = (start, end, factor) => start + (end - start) * factor;

    const debounce = (fn, ms = 150) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn(...args), ms);
        };
    };

    const calcTarget = (data) => {
        if (data.isLine) {
            let targetProgress = (currentScrollY - data.startScroll) / data.elementHeight;
            return Math.max(0, Math.min(1, targetProgress));
        } else {
            const totalDistance = viewportHeight + data.elementHeight || 1;
            const distanceScrolled = currentScrollY + viewportHeight - data.elementTop;
            let progress = Math.max(0, Math.min(1, distanceScrolled / totalDistance));
            return data.startTarget + (data.endTarget - data.startTarget) * progress;
        }
    };

    const applyStyles = (el, data) => {
        const val = data.currentVal;

        if (data.isLine) {
            el.style.transform = `scaleY(${val})`;
        } else {
            switch (data.type) {
                case "horizontal":
                    el.style.transform = `translate3d(${val}px, 0, 0)`;
                    break;
                case "scale":
                    el.style.transform = `scale(${1 + val / 100})`;
                    break;
                case "opacity":
                    el.style.opacity = Math.max(0, Math.min(1, 1 + val / 100));
                    break;
                default:
                    el.style.transform = `translate3d(0, ${val}px, 0)`;
                    break;
            }
        }
    };

    const initElement = (el, isResize = false) => {
        const isLine = el.classList.contains("line-parallax");
        const existingData = nodeData.get(el);

        const currentTransform = el.style.transform;
        el.style.transform = "none";

        const rect = el.getBoundingClientRect();
        const elementTop = rect.top + window.scrollY;
        const elementHeight = rect.height || (isLine ? 1 : 0);

        el.style.transform = currentTransform;

        const isVisible = existingData ? existingData.isVisible : false;
        let data = { elementTop, elementHeight, isLine, isVisible };

        if (isLine) {
            data.startScroll = elementTop - viewportHeight * 0.7;
        } else {
            const parallaxAttr = el.getAttribute("data-parallax") || "0,0";
            const parts = parallaxAttr.split(",");
            const sVal = parseFloat(parts[0]) || 0;
            const eVal = parts.length > 1 ? parseFloat(parts[1]) : sVal;

            data.startTarget = (sVal / 1000) * viewportHeight;
            data.endTarget = (eVal / 1000) * viewportHeight;
            data.type = el.getAttribute("data-parallax-type") || "vertical";
        }

        data.targetVal = calcTarget(data);

        if (!existingData || !isResize) {
            data.currentVal = data.targetVal;
            applyStyles(el, data);
        } else {
            data.currentVal = existingData.currentVal;
        }

        nodeData.set(el, data);
    };

    const trackObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const el = entry.target;
            const data = nodeData.get(el);
            if (!data) return;

            if (entry.isIntersecting) {
                if (!data.isVisible) {
                    data.targetVal = calcTarget(data);
                    data.currentVal = data.targetVal;
                    applyStyles(el, data);
                }

                data.isVisible = true;
                activeElements.add(el);
            } else {
                data.isVisible = false;
                const threshold = data.isLine ? 0.0001 : 0.05;
                if (Math.abs(data.targetVal - data.currentVal) < threshold) {
                    activeElements.delete(el);
                }
            }
        });
    }, { rootMargin: "200px 0px", threshold: 0 });

    const animateObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            entry.target.classList.toggle("visible", entry.intersectionRatio > 0.7);
        });
    }, { threshold: [0.7] });

    const render = () => {
        activeElements.forEach(el => {
            const data = nodeData.get(el);
            if (!data) return;

            data.targetVal = calcTarget(data);

            const friction = data.isLine ? 0.07 : 0.035;
            data.currentVal = lerp(data.currentVal, data.targetVal, friction);

            const threshold = data.isLine ? 0.0001 : 0.05;
            if (Math.abs(data.targetVal - data.currentVal) < threshold) {
                data.currentVal = data.targetVal;

                if (!data.isVisible) {
                    activeElements.delete(el);
                }
            }

            applyStyles(el, data);
        });

        requestAnimationFrame(render);
    };

    const observeElements = (container = document) => {
        container.querySelectorAll("[data-parallax], .line-parallax").forEach(el => {
            allElements.add(el);
            if (!nodeData.has(el)) initElement(el);
            trackObserver.observe(el);
        });
        container.querySelectorAll(".animate").forEach(el => animateObserver.observe(el));
    };

    new ResizeObserver(debounce(() => {
        viewportHeight = window.innerHeight;
        allElements.forEach(el => initElement(el, true));
    })).observe(document.body);

    let observerTimeout;
    const domObserver = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        for (const mutation of mutations) {
            if (mutation.addedNodes.length > 0) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === 1) {
                        shouldUpdate = true;
                        break;
                    }
                }
            }
            if (shouldUpdate) break;
        }

        if (shouldUpdate) {
            clearTimeout(observerTimeout);
            observerTimeout = setTimeout(() => observeElements(document), 50);
        }
    });

    domObserver.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", () => {
        currentScrollY = window.scrollY || window.pageYOffset;

        allElements.forEach(el => {
            if (!activeElements.has(el)) {
                const data = nodeData.get(el);
                if (data) {
                    data.targetVal = calcTarget(data);
                    data.currentVal = data.targetVal;
                    applyStyles(el, data);
                }
            }
        });
    }, { passive: true });

    observeElements();
    requestAnimationFrame(render);
})();